import { collection, doc, getDoc, getDocs, runTransaction } from 'firebase/firestore';
import { changeTripRoute } from '../services/tripRoute';
import { db } from '../services/firebase';
import { Day, TripConfig, Accommodation, Transport, Destination, Expense } from '../types';
import { stripUndef, writeDayChanges } from './dayWrites';
import { writeDestinationRemoval } from './destinationRemoval';
import { prepareTransportExpense, writeTransportBooking } from './transportExpenses';
import { getExchangeRate } from '../services/currencyService';
import { prepareAccommodationExpense, writeAccommodationBooking } from './accommodationExpenses';

export async function saveDestinationRemoval(tripId: string, previous: TripConfig, destination: Destination, userId: string) {
  // Through the server first: same removal (shared policy), plus a check that
  // the route left still holds every branch's split and rejoin (R3). The client
  // transaction below is the fallback only while the function is unreachable.
  const routed = await changeTripRoute({ tripId, action: 'remove', destination: stripUndef(destination) as unknown as Record<string, unknown>,
    previous: { destinations: previous.destinations as unknown as Record<string, unknown>[], startDate: previous.startDate, endDate: previous.endDate } });
  if (routed !== false) {
    const fresh = await getDoc(doc(db, 'trips', tripId));
    if (!fresh.exists()) throw new Error('This trip is no longer available.');
    return { ...previous, ...(fresh.data() as TripConfig), id: tripId };
  }
  const [days, schedules] = await Promise.all(['itinerary', 'schedules'].map(name => getDocs(collection(db, 'trips', tripId, name))));
  return runTransaction(db, transaction => writeDestinationRemoval(transaction, db, {
    tripId, previous, destination, userId, dayDates: days.docs.map(d => d.id), scheduleIds: schedules.docs.map(d => d.id),
  }));
}

export async function saveTransportBooking(trip: TripConfig, date: string, previous: Transport | null, next: Transport | null, userId: string) {
  const expense = next && !previous?.expenseLogId ? await prepareTransportExpense(trip, date, next, userId, getExchangeRate) : undefined;
  return runTransaction(db, transaction => writeTransportBooking(transaction, db, { tripId: trip.id, date, previous, next, userId, expense }));
}

export async function saveAccommodationBooking(trip: TripConfig, previous: Accommodation | null, next: Accommodation | null, userId: string, date?: string) {
  const expense = next && !previous?.expenseLogId ? await prepareAccommodationExpense(trip, next, userId, getExchangeRate, date) : undefined;
  return runTransaction(db, transaction => writeAccommodationBooking(transaction, db, { tripId: trip.id, previous, next, userId, expense, date }));
}

// Shared day-doc write path — extracted from ItineraryTab's local
// `saveDayUpdate` (Itinerary.tsx) so it can also be called from components
// that aren't mounted under the Itinerary/Shortlist/Planner tabs (e.g. a
// globally-mounted item modal opened from a notification while on Home).
// Apply only the user's delta against the latest documents. Separate return
// visits and gap days stay independent; overlapping edits fail visibly.
export async function saveDayUpdate(
  tripConfig: TripConfig,
  updated: Day,
  previousDay: Day,
  userId: string,
  expense?: Expense,
): Promise<void> {
  await runTransaction(db, transaction => writeDayChanges(transaction, db, {
    tripId: tripConfig.id, previous: previousDay, updated, userId, expenses: expense ? [expense] : [],
  }));
}

